"use client";

import type { ReactNode } from "react";

/**
 * AbhakZeile (Kantine Einkauf und Bedarf E1, Entscheidungsvorlage 2026-10-20 Kandidat 2A „Abhaken“, Idee 1: ein Abhak-Baustein für Einkauf UND
 * Brötchen-Küche B3; Design-Guide `abhak-zeile.md`, Musterkatalog M-118). Eine Zeile mit einem großen Haken (44 px, Daumen am Handy), Titel,
 * Unterzeile und freiem Bereich für Pillen, Hinweise und Textknöpfe (z. B. „Nicht bekommen“). Abgehakt: Titel durchgestrichen und gedämpft, die
 * Aussage trägt der Haken (nie nur Farbe).
 *
 * Der Haken ist eine echte Checkbox (`sr-only`, Name über `ariaLabel`), die sichtbare Fläche ist ihr Label; Fokusring am Label. Der Baustein prüft
 * keine Rechte: ohne Recht reicht der Aufrufer `gesperrt` (r110: Anbieten = Erlauben) und nennt den Grund am Element (r262). Er speichert nichts
 * selbst; `beimAbhaken` ruft die Action, Rückmeldung (Satz an der Zeile) gibt der Aufrufer über `meldung`.
 */
export function AbhakZeile({
  id,
  titel,
  unterzeile,
  abgehakt,
  beimAbhaken,
  ariaLabel,
  gesperrt = false,
  laedt = false,
  kinder,
  aktion,
  meldung,
}: {
  id: string;
  /** „3 × Gouda in Scheiben“ */
  titel: string;
  /** „Packung 400 g · Kühlschrank 1“ */
  unterzeile?: string;
  abgehakt: boolean;
  beimAbhaken?: (neu: boolean) => void;
  /** Name des Hakens für Hilfstechnik, z. B. „Gouda in Scheiben abhaken“. */
  ariaLabel: string;
  gesperrt?: boolean;
  laedt?: boolean;
  /** Pillen (Quelle), Hinweise („Vorschlag jetzt 2“), Zustand („Nicht bekommen“). */
  kinder?: ReactNode;
  /** Rechts oben: Menü oder Textknopf. */
  aktion?: ReactNode;
  /** Satz an der Zeile (Ablehnung oder Fehlschlag, r105), `role="status"`. */
  meldung?: ReactNode;
}) {
  const hakenId = `${id}-haken`;
  return (
    <>
      {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-checkbox-and-preference-controls/to-do-checkbox-list */}
      {/* Abweichung AW-127 (1): Haken als 44-px-Fläche (Daumen), Checkbox sr-only mit Fokusring am Label, freier Bereich für Pillen und Textknöpfe */}
      <div className={abgehakt ? "flex gap-x-3 py-3 px-4 bg-muted" : "flex gap-x-3 py-3 px-4 bg-layer"} data-abhak-zeile={abgehakt ? "abgehakt" : "offen"}>
        <input
          id={hakenId}
          type="checkbox"
          className="peer sr-only"
          checked={abgehakt}
          disabled={gesperrt || laedt}
          aria-label={ariaLabel}
          aria-busy={laedt || undefined}
          onChange={(e) => beimAbhaken?.(e.target.checked)}
        />
        <label
          htmlFor={hakenId}
          className={
            abgehakt
              ? "shrink-0 size-11 inline-flex justify-center items-center rounded-lg bg-primary text-primary-foreground cursor-pointer peer-focus-visible:ring-2 peer-focus-visible:ring-primary-focus peer-disabled:opacity-50 peer-disabled:cursor-default"
              : "shrink-0 size-11 inline-flex justify-center items-center rounded-lg bg-layer border border-line-3 text-transparent cursor-pointer hover:bg-muted-hover peer-focus-visible:ring-2 peer-focus-visible:ring-primary-focus peer-disabled:opacity-50 peer-disabled:cursor-default"
          }
        >
          <svg className="shrink-0 size-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </label>
        <div className="grow min-w-0">
          <div className="flex justify-between items-start gap-x-2">
            <div className="min-w-0">
              <p className={abgehakt ? "text-base text-muted-foreground-1 line-through" : "text-base text-foreground"}>{titel}</p>
              {unterzeile && <p className="text-xs text-muted-foreground-1">{unterzeile}</p>}
            </div>
            {aktion && <div className="shrink-0">{aktion}</div>}
          </div>
          {kinder && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{kinder}</div>}
          {meldung && (
            <div role="status" className="mt-1.5">
              {meldung}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
